import React, { useState, useEffect } from 'react';
import './SetTagPopup.css';
import { database } from '../firebaseConfig';
import { ref, get, update } from 'firebase/database';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

const SetTagPopup = ({ 
  email, 
  userId, 
  onClose, 
  onTagsUpdated 
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [currentTags, setCurrentTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const firestore = getFirestore();

  // Hilfsfunktion für konsistente E-Mail-ID
  const getEmailId = () => {
    const emailId = email.id || email.messageId || `temp_${Date.now()}`;
    return emailId;
  };

  // Lade verfügbare Tags und aktuelle Tags
  useEffect(() => {
    const loadTagsData = async () => {
      try {
        setLoading(true);
        
        // Lade verfügbare AI Tags aus der Datenbank
        const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
        const snapshot = await get(aiTagsRef);
        
        const availableTagsList = [];
        if (snapshot.exists()) {
          snapshot.forEach((childSnapshot) => {
            availableTagsList.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
        }
        
        setAvailableTags(availableTagsList);
        
        // Bereite aktuelle Tags vor - NACH dem Laden der verfügbaren Tags
        const emailTags = email.tags || [];
        
        let currentTagsList = [];
        
        if (Array.isArray(emailTags)) {
          // Array-Format (alte Struktur)
          currentTagsList = emailTags;
        } else if (typeof emailTags === 'object' && emailTags !== null) {
          // Object-Format - prüfe ob neue oder alte Struktur
          const tagValues = Object.values(emailTags);
          const firstValue = tagValues[0];
          
          if (typeof firstValue === 'boolean') {
            // Alte Struktur: {tagName: true} → konvertiere zu vollständigen Tag-Objekten
            currentTagsList = [];
            
            for (const [tagName, isActive] of Object.entries(emailTags)) {
              if (isActive) {
                // Finde das vollständige Tag-Objekt aus availableTags
                const matchingTag = availableTagsList.find(t => 
                  t.tag === tagName || t.name === tagName
                );
                
                if (matchingTag) {
                  currentTagsList.push(matchingTag);
                } else {
                  // Fallback: Erstelle minimales Tag-Objekt
                  const fallbackTag = {
                    tag: tagName,
                    name: tagName,
                    color: '#666',
                    icon: null
                  };
                  currentTagsList.push(fallbackTag);
                }
              }
            }
          } else {
            // Neue Struktur: {tagName: {tag: ..., color: ..., ...}}
            currentTagsList = Object.values(emailTags);
          }
        }
        
        setCurrentTags(currentTagsList.map(tag => {
          const processedTag = typeof tag === 'string' ? { tag: tag, name: tag } : tag;
          return processedTag;
        }));
        
      } catch (err) {
        console.error('Error loading tags:', err);
        setError('Failed to load tags');
      } finally {
        setLoading(false);
      }
    };

    if (userId && email) {
      loadTagsData();
    }
  }, [userId, email]);

  // Tag hinzufügen/auswählen (Single-Select)
  const handleSelectTag = (tagToSelect) => {
    // Single-Select: Ersetze alle aktuellen Tags mit dem neuen Tag
    setCurrentTags([tagToSelect]);
  };

  // Tag entfernen
  const handleRemoveTag = () => {
    setCurrentTags([]);
  };

  // Tags speichern
  const handleSaveTags = async () => {
    try {
      setSaving(true);
      setError('');
      
      const emailId = getEmailId();
      
      // Konvertiere Tags ins richtige Format für die Datenbank
      let tagsToSave = null;
      if (currentTags.length > 0) {
        // NEU: Speichere als Objekt mit Tag-Namen als Keys und vollständigen Tag-Objekten als Values
        tagsToSave = {};
        currentTags.forEach((tag) => {
          const tagName = tag.tag || tag.name;
          if (tagName) {
            // Speichere das vollständige Tag-Objekt, nicht nur boolean
            tagsToSave[tagName] = {
              tag: tagName,
              name: tag.name || tagName,
              color: tag.color || '#666',
              icon: tag.icon || null,
              id: tag.id || null,
              case: tag.case || tagName,
              description: tag.description || '',
              createdAt: tag.createdAt || new Date().toISOString()
            };
          }
        });
      } else {
      }
      
      // NEU: Update in Firestore statt RTDB
      let docRef;
      let collectionInfo = "";
      
      if (email.isSent || email.emailType === 'sent' || email.emailType === 'replied') {
        // Sent/Replied emails sind in world_sent
        docRef = doc(firestore, 'world_sent', userId, 'emails', emailId);
        collectionInfo = "world_sent";
      } else if (email.isSpam || email.emailType === 'spam') {
        // Spam emails sind in world_inbox mit isSpam flag
        docRef = doc(firestore, 'world_inbox', userId, 'emails', emailId);
        collectionInfo = "world_inbox (spam)";
      } else if (email.emailType === 'scheduled') {
        // Scheduled emails sind in world_scheduled_emails
        docRef = doc(firestore, 'world_scheduled_emails', userId, 'emails', emailId);
        collectionInfo = "world_scheduled_emails";
      } else if (email.emailType === 'safeguard') {
        // SafeGuard drafts sind in world_drafts
        docRef = doc(firestore, 'world_drafts', userId, 'emails', emailId);
        collectionInfo = "world_drafts";
      } else {
        // Normale Inbox emails sind in world_inbox
        docRef = doc(firestore, 'world_inbox', userId, 'emails', emailId);
        collectionInfo = "world_inbox";
      }

      await updateDoc(docRef, {
        tags: tagsToSave
      });
      
      // Callback aufrufen um Parent-Komponente zu informieren
      if (onTagsUpdated) {
        onTagsUpdated(emailId, tagsToSave);
      }
      
      // Popup schließen
      onClose();
      
    } catch (err) {
      console.error('Error saving tags:', err);
      
      let errorMessage = 'Failed to save tags';
      if (err.code === 'not-found') {
        errorMessage = 'Email document not found in database. Please reload the page.';
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your authentication.';
      } else if (err.message) {
        errorMessage = `Failed to save tags: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Alle Tags entfernen
  const handleRemoveAllTags = () => {
    setCurrentTags([]);
  };

  if (loading) {
    return (
      <div className="setTagPopupOverlay">
        <div className="setTagPopupContainer">
          <div className="setTagPopupLoading">
            <div className="setTagPopupSpinner"></div>
            <p>Loading tags...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="setTagPopupOverlay" onClick={onClose}>
      <div className="setTagPopupContainer" onClick={(e) => e.stopPropagation()}>
        <div className="setTagPopupHeader">
          <h3>Select Email Tag</h3>
          <button 
            className="setTagPopupCloseBtn" 
            onClick={onClose}
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="setTagPopupBody">
          {error && (
            <div className="setTagPopupError">
              {error}
            </div>
          )}

          {/* Aktuelle Tags Sektion */}
          <div className="setTagPopupSection">
            <div className="setTagPopupSectionHeader">
              <h4>Selected Tag</h4>
              {currentTags.length > 0 && (
                <button 
                  className="setTagPopupRemoveAllBtn"
                  onClick={handleRemoveAllTags}
                >
                  Remove Tag
                </button>
              )}
            </div>
            <div className="setTagPopupCurrentTags">
              {currentTags.length > 0 ? (
                currentTags.map((tag, index) => (
                  <div 
                    key={index} 
                    className="setTagPopupCurrentTag"
                    style={{
                      backgroundColor: tag.color || '#666',
                      color: '#ffffff'
                    }}
                  >
                    {tag.icon && (
                      <img 
                        src={tag.icon} 
                        alt=""
                        className="setTagPopupTagIcon"
                      />
                    )}
                    <span>{tag.tag || tag.name}</span>
                    <button
                      className="setTagPopupRemoveTagBtn"
                      onClick={() => handleRemoveTag()}
                      title="Remove tag"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                ))
              ) : (
                <p className="setTagPopupNoTags">No tag selected</p>
              )}
            </div>
          </div>

          {/* Verfügbare Tags Sektion */}
          <div className="setTagPopupSection">
            <h4>Available Tags (Select One)</h4>
            <div className="setTagPopupAvailableTags">
              {availableTags.length > 0 ? (
                availableTags.map((tag) => {
                  const isSelected = currentTags.some(currentTag => 
                    (currentTag.tag || currentTag.name) === tag.tag
                  );
                  
                  return (
                    <button
                      key={tag.id}
                      className={`setTagPopupAvailableTag ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectTag(tag)}
                      style={{
                        backgroundColor: tag.color || '#666',
                        color: '#ffffff',
                        opacity: isSelected ? 1 : 0.8,
                        border: isSelected ? '2px solid #fff' : '1px solid transparent'
                      }}
                    >
                      {tag.icon && (
                        <img 
                          src={tag.icon} 
                          alt=""
                          className="setTagPopupTagIcon"
                        />
                      )}
                      <span>{tag.tag || tag.name}</span>
                      {isSelected && (
                        <span className="setTagPopupSelected">✓</span>
                      )}
                    </button>
                  );
                })
              ) : (
                <p className="setTagPopupNoAvailableTags">No tags available. Create tags in settings first.</p>
              )}
            </div>
          </div>
        </div>

        <div className="setTagPopupFooter">
          <button 
            className="setTagPopupCancelBtn" 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="setTagPopupSaveBtn" 
            onClick={handleSaveTags}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetTagPopup; 